import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Paper,
    InputAdornment,
    Chip,
    useTheme
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import { useContactUpdate } from '../../../hooks/useContactUpdate';

const ContactTags = ({
    contact,
    onContactUpdate,
    onSnackbar,
    t = {},
    compact = false
}) => {
    const theme = useTheme();
    const { updateContactPartial } = useContactUpdate();
    // Tags editing state
    const [editingTags, setEditingTags] = useState(false);
    const [deletingTags, setDeletingTags] = useState(false);
    const [newTag, setNewTag] = useState('');

    // Handlers for tag editing
    const handleAddTag = async () => {
        if (!newTag.trim()) return;
        
        try {
            const existingTags = contact.tags || [];
            if (existingTags.some(tag => tag.toLowerCase() === newTag.trim().toLowerCase())) {
                onSnackbar({
                    open: true,
                    message: 'Tag already exists',
                    severity: 'warning'
                });
                return;
            }
            
            const updatedTags = [...existingTags, newTag.trim()];
            const updatedContact = await updateContactPartial(contact._id, {
                tags: updatedTags
            }, contact);
            
            onContactUpdate(updatedContact);
            setNewTag('');
            setEditingTags(false);
            setDeletingTags(false); // Выходим из режима удаления при добавлении
            
            onSnackbar({
                open: true,
                message: 'Tag added successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error adding tag:', error);
            onSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error adding tag',
                severity: 'error'
            });
        }
    };

    const handleRemoveTag = async (tagToRemove) => {
        try {
            const updatedTags = (contact.tags || []).filter(tag => tag !== tagToRemove);
            const updatedContact = await updateContactPartial(contact._id, {
                tags: updatedTags
            }, contact);
            
            onContactUpdate(updatedContact);
            
            // Автоматически выходим из режима удаления если тегов больше нет
            if (updatedTags.length === 0) {
                setDeletingTags(false);
            }
            
            onSnackbar({
                open: true,
                message: 'Tag removed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error removing tag:', error);
            onSnackbar({
                open: true,
                message: error.response?.data?.message || 'Error removing tag',
                severity: 'error'
            });
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: compact ? 'flex-start' : 'space-between', alignItems: 'center', gap: 1, mb: compact ? 0.75 : 2 }}>
                {!compact && (
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    <LocalOfferIcon fontSize="small" />
                    Tags
                </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {!editingTags && !deletingTags && contact.tags && contact.tags.length > 0 && (
                        <IconButton 
                            size="small" 
                            onClick={() => setDeletingTags(true)}
                            color="error"
                            title="Delete tags"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                    {!editingTags && !deletingTags && (
                        <IconButton 
                            size="small" 
                            onClick={() => setEditingTags(true)}
                            color="primary"
                            title="Add tag"
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    )}
                    {deletingTags && (
                        <IconButton 
                            size="small" 
                            onClick={() => setDeletingTags(false)}
                            color="primary"
                            title="Cancel"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>
            
            {editingTags && (
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        label="New Tag"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Enter tag name"
                        size="small"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleAddTag}
                                        disabled={!newTag.trim()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && newTag.trim()) {
                                handleAddTag();
                            }
                        }}
                        sx={{ mb: 2 }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            setEditingTags(false);
                            setNewTag('');
                            setDeletingTags(false);
                        }}
                    >
                        Cancel
                    </Button>
                </Box>
            )}
            
            {contact.tags && contact.tags.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: compact ? 0.75 : 1.5 }}>
                    {contact.tags.map((tag, index) => (
                        <Chip 
                            key={`tag-${tag}-${index}`} 
                            label={tag} 
                            size={compact ? 'small' : 'medium'}
                            color={deletingTags ? "error" : "primary"}
                            variant="filled"
                            onDelete={deletingTags ? () => handleRemoveTag(tag) : undefined}
                            deleteIcon={deletingTags ? <DeleteIcon /> : undefined}
                            sx={{
                                ...(deletingTags && {
                                    background: theme.palette.mode === 'light'
                                        ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
                                        : 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
                                    color: theme.palette.mode === 'light' ? '#c62828' : '#ffffff',
                                    border: theme.palette.mode === 'light'
                                        ? '1px solid rgba(211, 47, 47, 0.3)'
                                        : '1px solid rgba(239, 68, 68, 0.4)',
                                    '&:hover': {
                                        background: theme.palette.mode === 'light'
                                            ? 'linear-gradient(135deg, #ffcdd2 0%, #ef9a9a 100%)'
                                            : 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: theme.palette.mode === 'light'
                                            ? '0 2px 8px rgba(211, 47, 47, 0.3)'
                                            : '0 2px 8px rgba(239, 68, 68, 0.4)',
                                        '& .MuiChip-deleteIcon': {
                                            color: 'error.main'
                                        }
                                    }
                                }),
                                ...(!deletingTags && {
                                    variant: 'outlined',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.04)'
                                    }
                                })
                            }}
                        />
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: compact ? 'none' : 'block' }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                        No tags added
                    </Typography>
                </Box>
            )}

            {deletingTags && (
                <Box sx={{ mt: compact ? 1 : 2, p: compact ? 1 : 2, bgcolor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                    <Typography variant="body2" color="error.contrastText" sx={{ fontWeight: 600 }}>
                        🗑️ Delete Mode: Click on any tag to remove it
                    </Typography>
                    <Typography variant="caption" color="error.contrastText" sx={{ display: 'block', mt: 0.5 }}>
                        Be careful! This action cannot be undone.
                    </Typography>
                </Box>
            )}

        </Box>
    );
};

export default ContactTags; 
